
import React, { useEffect, useState, useMemo, useRef } from "react";
import { apiGet, apiPost, apiPut } from "../api/http";
import "../styles/categories.css";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { dateFormat } from "../utils/dateFormat";
import dayjs from "dayjs";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Utility hook to check if text is truncated
function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      setTruncated(el.scrollWidth > el.clientWidth);
    }
  });
  return [ref, truncated] as const;
}

// Row component for table to use hooks safely
const CategoryTableRow: React.FC<{ item: Category; openEditModal: (item: Category) => void }> = ({ item, openEditModal }) => {
  const [createdByRef, createdByTruncated] = useIsTruncated<HTMLSpanElement>();
  const [modifiedByRef, modifiedByTruncated] = useIsTruncated<HTMLSpanElement>();
  return (
    <tr key={item.categoryId}>
      <td>
        {item.categoryName?.replace(/\b\w/g, char => char.toUpperCase())}
      </td>
      <td>
        <Tooltip
          title={item.createdBy}
          arrow
          disableHoverListener={!createdByTruncated}
        >
          <span
            ref={createdByRef}
            style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
          >
            {item.createdBy}
          </span>
        </Tooltip>
      </td>
      <td>
        {dateFormat(item.createdDate)}
      </td>
      <td>
        <Tooltip
          title={item.modifiedBy || ''}
          arrow
          disableHoverListener={!modifiedByTruncated}
        >
          <span
            ref={modifiedByRef}
            style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
          >
            {item.modifiedBy ?? "-"}
          </span>
        </Tooltip>
      </td>
      <td>
        {dateFormat(item.modifiedDate) ?? "-"}
      </td>
      <td>
        <span
          className={
            item.status === "Active"
              ? "badge badge-active"
              : "badge badge-inactive"
          }
        >
          {item.status}
        </span>
      </td>
      <td className="action-cell">
        <Tooltip title="Edit Category" arrow>
          <button
            className="edit-btn"
            onClick={() => openEditModal(item)}
            tabIndex={0}
          >
            <LocalIcon src="/icons/EditIcon.svg" className="edit-icon" />
          </button>
        </Tooltip>
      </td>
    </tr>
  );
};

type Category = {
  categoryId: number;
  categoryName: string;
  createdBy: string;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
  status: string;
};

type LocalIconProps = {
    src: string;
    alt?: string;
    className?: string;
};

const LocalIcon: React.FC<LocalIconProps> = ({ src, alt = "", className }) => (
    <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
    />
);

const PAGE_SIZE = 10;

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<keyof Category | null>("createdDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Category | null>(null);
  const [name, setName] = useState("");
  // Status filter for table: null = placeholder, 'Active'/'Inactive' = selected
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  // Separate input value for Autocomplete (for clear icon and typing)
  const [filterStatusInput, setFilterStatusInput] = useState<string>("");
  // Status for modal form: always string
  const [status, setStatus] = useState<string>("Active");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [nameError, setNameError] = useState("");
    const [statusError, setStatusError] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!showModal) return;

    // Ensure keyboard focus enters the modal when it opens.
    requestAnimationFrame(() => {
      nameInputRef.current?.focus();
    });
  }, [showModal]);

  const handleModalKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !modalRef.current) return;

    const focusable = Array.from(
      modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (e.shiftKey) {
      if (active === first || !modalRef.current.contains(active)) {
        e.preventDefault();
        last.focus();
      }
      return;
    }

    if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

const fetchCategories = async () => {
  try {
      const data = await apiGet<Category[]>('/activity-categories');
    setCategories(data ?? []);
  } catch (error) {
    console.error('Failed to load categories', error);
  } finally {
    setLoading(false);
  }
};
    const filteredData = useMemo(() => {
      let data = categories;
      // Only filter by status if a value is selected
      if (filterStatus) {
        data = data.filter((item: any) => item.status === filterStatus);
      }
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        data = data.filter((item: any) => {
          return Object.entries(item).some(([key, val]: any) => {
            if (!val) return false;
            if (key.toLowerCase().includes("date")) {
              let formatted = "";
              if (
                typeof val === "string" ||
                val instanceof Date ||
                val === null ||
                val === undefined
              ) {
                formatted = dateFormat(val)?.toLowerCase() || "";
              }
              return (
                formatted.includes(lower) ||
                String(val).toLowerCase().includes(lower)
              );
            }
            return String(val).toLowerCase().includes(lower);
          });
        });
      }
      return data;
    }, [categories, searchTerm, filterStatus]);
            const sortedData = useMemo(() => {

    let data = [...filteredData];

    // Default latest created date first
    if (!sortColumn) {
        return data.sort(
            (a, b) =>
                new Date(b.createdDate ?? 0).getTime() -
                new Date(a.createdDate ?? 0).getTime()
        );
    }

    data.sort((a: any, b: any) => {

        let valA = a?.[sortColumn];
        let valB = b?.[sortColumn];

        // null handling
        if (valA == null) return 1;
        if (valB == null) return -1;

        // DATE sort
        if (String(sortColumn).toLowerCase().includes("date")) {

            const parseDate = (v: any) => {

                if (!v) return -Infinity;

                let d = dayjs(v);

                if (!d.isValid() && typeof v === "string") {
                    d = dayjs(v, "DD-MMM-YYYY");
                }

                return d.isValid() ? d.valueOf() : -Infinity;
            };

            const t1 = parseDate(valA);
            const t2 = parseDate(valB);

            return sortDirection === "asc"
                ? t1 - t2
                : t2 - t1;
        }

        // NUMBER sort
        if (!isNaN(valA) && !isNaN(valB)) {
            return sortDirection === "asc"
                ? Number(valA) - Number(valB)
                : Number(valB) - Number(valA);
        }

        // STATUS sort
        if (sortColumn === "status") {

            const orderMap: any = {
                Active: 1,
                Inactive: 2
            };

            return sortDirection === "asc"
                ? orderMap[valA] - orderMap[valB]
                : orderMap[valB] - orderMap[valA];
        }

        // STRING sort
        return sortDirection === "asc"
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));

    });

    return data;

}, [filteredData, sortColumn, sortDirection]);

    const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);

    const paginatedData = sortedData.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    const handleSort = (column: keyof Category) => {
        setCurrentPage(1);   // ⭐ important
        // Disable sorting if there are no records
        if (sortedData.length === 0) return;
        if (sortColumn === column) {
          setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
          setSortColumn(column);
          setSortDirection("asc");
        }
      };

  const renderSortIcon = (column: keyof Category) => {
    // Disable sort icons if there are no records
    if (sortedData.length === 0) {
      return <span className="sort-icons disabled"><ArrowUpwardIcon className="sort-icon" /><ArrowDownwardIcon className="sort-icon" /></span>;
    }
    const isActive = sortColumn === column;
    return (
      <span className="sort-icons">
        <ArrowUpwardIcon
          className={`sort-icon ${
            isActive && sortDirection === "asc" ? "active" : ""
          }`}
        />
        <ArrowDownwardIcon
          className={`sort-icon ${
            isActive && sortDirection === "desc" ? "active" : ""
          }`}
        />
      </span>
    );
  };
    const resetForm = () => {
        setName("");
      setStatus("");
        setEditItem(null);
        setError("");
        setNameError("");
        setStatusError("");
    };
    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };

  const openEditModal = (item: Category) => {
    resetForm();   // IMPORTANT
    setEditItem(item);
    setName(item.categoryName);
    setStatus(item.status);
    setShowModal(true);
  };

  const normalizeCategoryName = (value: string) => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[\s\/\-]+/g, ""); // remove space, /, - completely
};

    const handleSave = async () => {
      if (isSaving) return;
        if (!validateForm()) return;
    const trimmedName = name.trim();
    setIsSaving(true);
    try {
      let response: Category;
      if (editItem) {
        response = await apiPut(`/activity-categories/${editItem.categoryId}`, {
          name: trimmedName,
          status,
        });
        setCategories(prev =>
            prev.map(c => c.categoryId === response.categoryId ? response : c)
        );
        // Do NOT reset page after edit
        setBanner({ type: "success", message: "Category has been updated successfully" });
        setSnackbarOpen(true);
      } else {
        response = await apiPost('/activity-categories', {
          name: trimmedName,
          status,
        });
        setCategories(prev => [response, ...prev]);
        setSearchTerm(""); // Clear search
        setFilterStatus(null); // Clear filter
        setCurrentPage(1);   // ⭐ reset to first page after add
        setBanner({ type: "success", message: "New category has been added successfully" });
        setSnackbarOpen(true);
      }
      setShowModal(false);
      setEditItem(null);
      setName("");
      setStatus("");
      setTimeout(() => setBanner(null), 3000);
    } catch (err) {
      setBanner({ type: "error", message: "An error occurred. Please try again." });
      setSnackbarOpen(true);
      console.error('Save failed', err);
    } finally {
      setIsSaving(false);
    }
  };

    const validateForm = () => {
        let valid = true;

        const trimmed = name.trim();

        // Name required
        if (!trimmed) {
          setNameError("Category name is required");
          valid = false;
        }
        // min length
        else if (trimmed.length < 3) {
          setNameError("Minimum 3 characters required");
          valid = false;
        }
        // max length
        else if (trimmed.length > 30) {
          setNameError("Please limit to 30 characters");
          valid = false;
        }
        // cannot be only numbers or only special characters (including allowed special chars)
        else if (!/[a-zA-Z]/.test(trimmed)) {
          setNameError("Category name cannot be only numbers or special characters");
          valid = false;
        }
        // if contains at least one letter but also invalid special character (not a-z, A-Z, 0-9, space, /, -)
        else if (/[a-zA-Z]/.test(trimmed) && /[^a-zA-Z0-9\s\/\-]/.test(trimmed)) {
          setNameError("Only letters, numbers, spaces, / and - are allowed");
          valid = false;
        }
        else {
          setNameError("");
        }

        // Duplicate check
        const normalizedInput = normalizeCategoryName(name.trim());

        const duplicate = categories.some(c => {
           const existing = normalizeCategoryName((c.categoryName ?? "").toString());
            return (
                existing === normalizedInput &&
                (!editItem || c.categoryId !== editItem.categoryId)
            );
        });

        if (duplicate) {
            setNameError("Category already exists");
            valid = false;
        }

        // Status validation
        if (!status) {
            setStatusError("Status required");
            valid = false;
        } else {
            setStatusError("");
        }

        return valid;
    };
  if (loading) return <div className="categories-loading">Loading...</div>;

  return (
      <div className="categories-container categories-layout">
    <div className="categories-top">
      <h2 className="categories-heading">Categories</h2>

      <div className="categories-controls">
        <button className="btn-primary" onClick={openAddModal}>
          <LocalIcon src="/icons/AddIcon.svg" className="btn-icon" />
          Add Category
        </button>

        {/* Status Filter Dropdown - use default MUI clear icon, style to match search field */}
        <Autocomplete
          className="filter-autocomplete status-filter"
          options={Array.from(new Set(categories.map((item) => item.status)))}
          value={filterStatus}
          inputValue={filterStatusInput}
          forcePopupIcon
          clearIcon={null}
          onInputChange={(_event, newInputValue) => {
            setFilterStatusInput(newInputValue);
          }}
          onChange={(_event, newValue) => {
            setFilterStatus(newValue);
            setFilterStatusInput(newValue || "");
            setCurrentPage(1);
          }}
          filterOptions={(options, { inputValue }) =>
            options.filter(option =>
              option.toLowerCase().includes(inputValue.toLowerCase())
            )
          }
          size="small"
          clearOnEscape
          sx={{
            '& .MuiAutocomplete-clearIndicator': {
              color: '#6b7280',
              padding: 0,
              marginRight: '2px',
              '&:hover': {
                background: 'rgba(0,0,0,0.04)',
                color: '#6b7280',
              },
              '& svg': {
                fontSize: '18px',
                fontWeight: 400,
              },
            },
            '@media (prefers-color-scheme: dark)': {
              '& .MuiAutocomplete-popupIndicator': { color: '#fff' },
              '& .MuiAutocomplete-noOptions': { color: '#fff' },
              '& .MuiAutocomplete-clearIndicator': { color: '#fff' },
            },
          }}
          renderInput={(params) => {
            const { InputProps, ...rest } = params;
            return (
              <TextField
                {...rest}
                InputProps={{
  ...InputProps,
  endAdornment: (
    <>
     {(filterStatusInput || filterStatus) && (
  <IconButton
    size="small"
    aria-label="clear status filter"
    onClick={() => {
      setFilterStatus(null);
      setFilterStatusInput("");
      setCurrentPage(1);
    }}
    edge="end"
    sx={{
      padding: '2px',
      marginLeft: '36px',
      '@media (prefers-color-scheme: dark)': {
        color: '#fff',
        '& .MuiSvgIcon-root': {
          color: '#fff',
        },
      },
    }}
  >
    <ClearIcon fontSize="small" />
  </IconButton>
)}

      {InputProps.endAdornment}
    </>
  )
}}
                placeholder="Status"
                variant="outlined"
                className="filter-input"
              />
            );
          }}
        />

        <TextField
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="input-search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="search-icon" />
              </InputAdornment>
            ),
            endAdornment: (
              searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                    edge="end"
                    sx={{
                      '@media (prefers-color-scheme: dark)': {
                        color: '#fff',
                        '& .MuiSvgIcon-root': {
                          color: '#fff',
                        },
                      },
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            ),
          }}
        />
      </div>
    </div>

      <div className="table-card">
        <table className="categories-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("categoryName")}>
                <div className="th-inner">
                  Name {renderSortIcon("categoryName")}
                </div>
              </th>
              <th onClick={() => handleSort("createdBy")}>
                <div className="th-inner">
                  Created By {renderSortIcon("createdBy")}
                </div>
              </th>
              <th onClick={() => handleSort("createdDate")}>
                <div className="th-inner">
                  Created Date {renderSortIcon("createdDate")}
                </div>
              </th>
              <th onClick={() => handleSort("modifiedBy")}>
                <div className="th-inner">
                  Updated By {renderSortIcon("modifiedBy")}
                </div>
              </th>
              <th onClick={() => handleSort("modifiedDate")}>
                <div className="th-inner">
                  Updated Date {renderSortIcon("modifiedDate")}
                </div>
              </th>
              <th onClick={() => handleSort("status")}> <div className="th-inner">Status {renderSortIcon("status")}</div> </th>
              <th className="action-header">Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-state">
                     <div className="empty-content">
                       <p className="th-inner">No Categories found.<br/>
                      Try a different search or add a new category.
                       </p>
                     </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => (
                <CategoryTableRow key={item.categoryId} item={item} openEditModal={openEditModal} />
              ))
            )}
          </tbody>
        </table>
    </div>


    {/* Pagination - only show if there are records */}
    {sortedData.length > 0 && (
      <div className="pagination-container">
        <div className="pagination-info">
          {sortedData.length === 0
            ? 0
            : (currentPage - 1) * PAGE_SIZE + 1}{" "}
          to{" "}
          {Math.min(currentPage * PAGE_SIZE, sortedData.length)}{" "}
          of {sortedData.length}  | Page {currentPage} of {totalPages || 1}
        </div>
        <button
          className="page-btn first-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(1)}
        >
          First
        </button>
        <button
          className="page-btn prev-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </button>
        <div className="page-numbers">
          {(() => {
            const blockSize = 5;
            const currentBlock = Math.floor((currentPage - 1) / blockSize);
            const startPage = currentBlock * blockSize + 1;
            const endPage = Math.min(startPage + blockSize - 1, totalPages);
            const pageButtons = [];
            for (let p = startPage; p <= endPage; p++) {
              pageButtons.push(
                <button
                  key={p}
                  className={`page-number ${p === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              );
            }
            return pageButtons;
          })()}
        </div>
        <button
          className="page-btn next-btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage((p) => Math.min(totalPages || 1, p + 1))}
        >
          Next
        </button>
        <button
          className="page-btn last-btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(totalPages)}
        >
          Last
        </button>
      </div>
    )}

    {showModal && (
      <div className="modal-overlay">
        <div
          className="modal"
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="category-modal-title"
          onKeyDown={handleModalKeyDown}
        >
          <h3 id="category-modal-title">{editItem ? "Edit Category" : "Add Category"}</h3>
          <label>
            Category Name <span className="required">*</span>
          </label>
          <div style={{ position: 'relative' }}>
            <input
              ref={nameInputRef}
              value={name}
              maxLength={30}
              onChange={(e) => {
                setName(e.target.value.replace(/\s{2,}/g, " "));
                if (nameError) setNameError("");
              }}
              placeholder="Category Name"
              style={{ paddingRight: name ? 32 : undefined }}
            />
            {name && (
              <button
                type="button"
                aria-label="clear category name"
                onClick={() => setName("")}
                style={{
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 2,
                  ...(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
                    ? { color: '#f1f5f9' }
                    : {}),
                }}
              >
                <ClearIcon fontSize="small" />
              </button>
            )}
          </div>
          <div
            style={{
              fontSize: '12px',
              color: (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? '#fff' : '#000',
              marginTop: 2,
              marginBottom: 2
            }}
          >
            {name.length}/30 characters
          </div>
          {nameError && (
            <div className="field-error">
              {nameError}
            </div>
          )}
                      <label>
                          Status <span className="required">*</span>
                      </label>
                        <select
                          value={status ?? ''}
                          onChange={(e) => {
                            setStatus(e.target.value);
                            if (statusError) setStatusError("");
                          }}
                          style={{
                            color: undefined,
                            background: undefined,
                            // Only apply color in dark mode
                            ...(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
                              ? { color: '#fff', background: '#232c3a' }
                              : {}),
                          }}
                        >
                          <option value="">Select Status</option>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                      </select>

                      {statusError && (
                          <div className="field-error">
                              {statusError}
                          </div>
                      )}
                      {error && <div className="field-error">{error}</div>}
          <div className="modal-actions">
            <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : (editItem ? "Update" : "Create")}
            </button>
           <button onClick={() => {
              resetForm();
              setShowModal(false);
            }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    <Snackbar
      open={snackbarOpen && !!banner}
      autoHideDuration={4000}
      onClose={() => { setSnackbarOpen(false); setBanner(null); }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ zIndex: 1400 }}
    >
      <Alert
        onClose={() => { setSnackbarOpen(false); setBanner(null); }}
        severity={banner?.type || 'success'}
        icon={banner?.type === 'success' ? <CheckCircleIcon fontSize="inherit" sx={{ color: '#2e7d32' }} /> : undefined}
        sx={{
          width: '100%',
          fontWeight: 500,
          fontSize: 15,
          alignItems: 'center',
          backgroundColor: banner?.type === 'success' ? '#e6f4ea' : '#fdecea',
          color: banner?.type === 'success' ? '#2e7d32' : '#d32f2f',
          boxShadow: 3,
          '@media (prefers-color-scheme: dark)': {
            backgroundColor: banner?.type === 'success' ? '#1b2e1b' : '#2d1616',
            color: banner?.type === 'success' ? '#7ee787' : '#ffb4ab',
          },
        }}
      >
        <span className="app-snackbar-message">{banner?.message || ''}</span>
      </Alert>
    </Snackbar>
  </div>
);

};

export default Categories;
